// Phase 6 — Portal "Executed Documents" tile.
//
// Fetches `/api/portal/projects/{pid}/executed-documents` and renders a
// list of client-visible executed instruments (FDA, Contract Record,
// Appointment, Change) with the structured signer evidence captured in
// Phase 1–5.  Download link routes through the scoped download endpoint
// that re-asserts category + parent-visibility before streaming.
//
// Placement: tile is a drop-in <ExecutedDocumentsTile projectId={...}
// filter={?} /> and should be used on existing portal pages (Overview,
// Contract Position, Contract Change) rather than a new standalone route.
import React, { useEffect, useState } from "react";
import { portalApi } from "./portalApi";
import { Card, CardHeader, Pill, Spinner, EmptyState } from "./PortalUi";

const KIND_LABELS = {
    free_design_agreement: "Design Agreement",
    contract_record: "Contract",
    paid_service_appointment: "Appointment",
    contract_change: "Change",
};

const KIND_TONES = {
    free_design_agreement: "accent",
    contract_record: "ok",
    paid_service_appointment: "info",
    contract_change: "warn",
};

const METHOD_LABELS = {
    zoho_sign: "Zoho Sign",
    docusign: "DocuSign",
    hand_signed: "Hand signed",
    other_e_sign: "Other e-sign",
    other: "Other",
    external_upload: "External upload",
    typed: "Typed signature",
};

function fmtDateShort(v) {
    if (!v) return "—";
    try {
        const s = String(v).replace("Z", "+00:00");
        const d = new Date(s);
        if (isNaN(d.getTime())) return String(v).slice(0, 10);
        return d.toLocaleDateString("en-GB",
            { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return String(v).slice(0, 10);
    }
}

function fmtSize(bytes) {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function signedRange(signers) {
    const dates = (signers || [])
        .map(s => s.signed_on).filter(Boolean).sort();
    if (dates.length === 0) return null;
    if (dates.length === 1 || dates[0] === dates[dates.length - 1]) {
        return `Signed ${fmtDateShort(dates[0])}`;
    }
    return `Signed ${fmtDateShort(dates[0])} → ${fmtDateShort(dates[dates.length - 1])}`;
}

export default function ExecutedDocumentsTile({ projectId, filter }) {
    const [resolvedPid, setResolvedPid] = useState(projectId || null);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [reloadTick, setReloadTick] = useState(0);
    const [expandedPrior, setExpandedPrior] = useState({});

    useEffect(() => {
        if (projectId) { setResolvedPid(projectId); return; }
        let cancelled = false;
        portalApi.me()
            .then(r => { if (!cancelled) setResolvedPid(r.data?.project_id); })
            .catch(() => { if (!cancelled) setErr("Session expired."); });
        return () => { cancelled = true; };
    }, [projectId]);

    useEffect(() => {
        if (!resolvedPid) return;
        let cancelled = false;
        setErr("");
        setData(null);
        portalApi.executedDocuments(resolvedPid)
            .then(r => { if (!cancelled) setData(r.data); })
            .catch(e => {
                if (cancelled) return;
                setErr(e?.response?.data?.detail
                       || "Unable to load executed documents");
            });
        return () => { cancelled = true; };
    }, [resolvedPid, reloadTick]);

    if (err) {
        return (
            <Card testid="portal-executed-documents-err">
                <CardHeader title="Executed documents" subtle />
                <div className="p-5 text-[12.5px] text-[#8C2E1E]">
                    {err}{" "}
                    <button
                        type="button"
                        onClick={() => setReloadTick(t => t + 1)}
                        data-testid="portal-executed-documents-retry"
                        className="underline text-[#A8543F] font-manrope font-semibold">
                        Retry
                    </button>
                </div>
            </Card>);
    }
    if (!data) {
        return <Spinner testid="portal-executed-documents-loading" />;
    }
    let rows = data.executed_documents || [];
    if (filter) rows = rows.filter(filter);
    const empty = rows.length === 0;

    // Phase A multi-file: the endpoint still emits one row per file
    // (file-flat), so a bundle of N attached PDFs produces N rows that
    // share `instrument_kind` + `instrument_id` + `signers` +
    // `method_summary` + `external_ref` but differ on `file_id` /
    // `filename` / `size`.  Group them client-side so the client sees
    // one card per instrument with N nested attachment rows.  A row
    // that lacks either grouping key falls into a singleton group
    // (preserves pre-multi-file behaviour cleanly).
    const groups = [];
    const groupIndex = new Map();
    rows.forEach(r => {
        const key = (r.instrument_kind && r.instrument_id)
            ? `${r.instrument_kind}:${r.instrument_id}`
            : `__singleton:${r.file_id}`;
        let idx = groupIndex.get(key);
        if (idx == null) {
            idx = groups.length;
            groupIndex.set(key, idx);
            groups.push({
                key,
                instrument_kind: r.instrument_kind,
                instrument_id: r.instrument_id,
                instrument_label: r.instrument_label,
                signers: r.signers,
                method_summary: r.method_summary,
                external_ref: r.external_ref,
                files: [],
                _latest_created_at: null,
            });
        }
        groups[idx].files.push(r);
        const t = r.latest_version?.uploaded_at || r.created_at || null;
        if (t && (!groups[idx]._latest_created_at
                  || t > groups[idx]._latest_created_at)) {
            groups[idx]._latest_created_at = t;
        }
    });
    // Preserve the server's `created_at DESC` ordering: the server
    // already returns newest first, so the first group encountered is
    // the newest.  A deterministic stable sort by latest file date
    // keeps parity when the endpoint is updated to interleave.
    groups.sort((a, b) => {
        if (!a._latest_created_at && !b._latest_created_at) return 0;
        if (!a._latest_created_at) return 1;
        if (!b._latest_created_at) return -1;
        return a._latest_created_at < b._latest_created_at ? 1 : -1;
    });

    return (
        <Card testid="portal-executed-documents">
            <CardHeader title="Executed documents" subtle />
            <div className="px-5 pt-3 pb-1 text-[12px] text-[#6B6B67]">
                Documents that have been signed by all parties.
            </div>
            {empty ? (
                <div className="px-5 pb-5">
                    <EmptyState
                        heading="No executed documents yet."
                        note={"When your agreement or contract is signed, "
                              + "the executed PDF will appear here for "
                              + "download."}
                        testid="portal-executed-documents-empty" />
                </div>
            ) : (
                <div className="divide-y divide-[#EEEBE4]">
                    {groups.map((g, gIdx) => {
                        const isSingleton = g.files.length === 1;
                        const headRow = g.files[0];
                        const groupTid = `portal-exec-group-${gIdx}`;
                        return (
                            <div key={g.key}
                                 className="px-5 py-4"
                                 data-testid={groupTid}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Pill tone={KIND_TONES[g.instrument_kind] || "neutral"}
                                                  testid={`${groupTid}-kind`}>
                                                {KIND_LABELS[g.instrument_kind] || g.instrument_kind}
                                            </Pill>
                                            <span className="font-manrope text-[14px] text-charcoal"
                                                  data-testid={`${groupTid}-label`}>
                                                {g.instrument_label}
                                            </span>
                                            {!isSingleton && (
                                                <span className="inline-flex items-center px-2 py-[1px] rounded-full bg-[#F2EEE6] text-[10.5px] font-manrope font-semibold text-[#6B6B67]"
                                                      data-testid={`portal-exec-group-file-count-${gIdx}`}>
                                                    {g.files.length} files
                                                </span>
                                            )}
                                        </div>
                                        {signedRange(g.signers) && (
                                            <div className="mt-1 text-[11.5px] text-[#6B6B67]"
                                                 data-testid={`${groupTid}-range`}>
                                                {signedRange(g.signers)}
                                            </div>
                                        )}
                                        {(g.signers || []).length > 0 && (
                                            <ul className="mt-2 space-y-1 text-[11.5px] text-[#4A4A46]"
                                                data-testid={`${groupTid}-signers`}>
                                                {g.signers.map((s, i) => (
                                                    <li key={i} className="flex items-center flex-wrap gap-2"
                                                        data-testid={`${groupTid}-signer-${i}`}>
                                                        <span className="text-charcoal">{s.signer_name}</span>
                                                        <span className="text-[#8A8A84]">· {s.signer_role}</span>
                                                        <span className="inline-flex items-center px-2 py-[1px] rounded-full bg-[#F2EEE6] text-[10px] font-manrope font-semibold uppercase tracking-[0.04em] text-[#6B6B67]">
                                                            {METHOD_LABELS[s.signature_method] || s.signature_method}
                                                        </span>
                                                        <span className="text-[#8A8A84]">· {fmtDateShort(s.signed_on)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {g.method_summary && (
                                            <div className="mt-2 text-[11.5px] text-[#6B6B67]"
                                                 data-testid={`${groupTid}-method`}>
                                                <span className="font-manrope font-semibold text-[#4A4A46]">Method:</span>{" "}
                                                {g.method_summary}
                                            </div>
                                        )}
                                        {g.external_ref && (
                                            <div className="mt-1 text-[11.5px] text-[#6B6B67]"
                                                 data-testid={`${groupTid}-ext-ref`}>
                                                <span className="font-manrope font-semibold text-[#4A4A46]">Reference:</span>{" "}
                                                {g.external_ref}
                                            </div>
                                        )}
                                    </div>
                                    {isSingleton && (
                                        <div className="shrink-0 text-right">
                                            <a
                                                href={portalApi.executedDocumentDownloadUrl(headRow.file_id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`portal-executed-doc-${headRow.file_id}-download`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#A8543F] hover:bg-[#8B4535] text-white font-manrope font-semibold text-[12.5px]">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                     stroke="currentColor" strokeWidth="2"
                                                     strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                                Download
                                            </a>
                                            {headRow.latest_version?.version_number != null && (
                                                <div className="mt-1 text-[10.5px] text-[#8A8A84]"
                                                     data-testid={`portal-executed-doc-${headRow.file_id}-latest-version`}>
                                                    v{headRow.latest_version.version_number} · {fmtSize(headRow.latest_version.size ?? headRow.size)}
                                                    {headRow.latest_version.uploaded_at ? ` · ${fmtDateShort(headRow.latest_version.uploaded_at)}` : ""}
                                                </div>
                                            )}
                                            {headRow.latest_version?.version_number == null && headRow.size != null && (
                                                <div className="mt-1 text-[10.5px] text-[#8A8A84]"
                                                     data-testid={`portal-executed-doc-${headRow.file_id}-size`}>
                                                    {fmtSize(headRow.size)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!isSingleton && (
                                    <ul className="mt-3 space-y-2 pl-3 border-l-2 border-[#EEEBE4]"
                                        data-testid={`${groupTid}-files`}>
                                        {g.files.map((f, fIdx) => (
                                            <li key={f.file_id}
                                                className="flex items-start justify-between gap-3 flex-wrap"
                                                data-testid={`portal-exec-group-file-row-${gIdx}-${fIdx}`}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-manrope text-[12.5px] text-charcoal break-all"
                                                         data-testid={`portal-exec-group-file-name-${gIdx}-${fIdx}`}>
                                                        {f.filename || f.file_id}
                                                    </div>
                                                    {f.latest_version?.version_number != null ? (
                                                        <div className="text-[10.5px] text-[#8A8A84]"
                                                             data-testid={`portal-exec-group-file-meta-${gIdx}-${fIdx}`}>
                                                            v{f.latest_version.version_number} · {fmtSize(f.latest_version.size ?? f.size)}
                                                            {f.latest_version.uploaded_at ? ` · ${fmtDateShort(f.latest_version.uploaded_at)}` : ""}
                                                        </div>
                                                    ) : (f.size != null && (
                                                        <div className="text-[10.5px] text-[#8A8A84]"
                                                             data-testid={`portal-exec-group-file-meta-${gIdx}-${fIdx}`}>
                                                            {fmtSize(f.size)}
                                                        </div>
                                                    ))}
                                                </div>
                                                <a
                                                    href={portalApi.executedDocumentDownloadUrl(f.file_id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`portal-exec-group-file-download-${gIdx}-${fIdx}`}
                                                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#A8543F] hover:bg-[#8B4535] text-white font-manrope font-semibold text-[12px]">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                                         stroke="currentColor" strokeWidth="2"
                                                         strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                        <polyline points="7 10 12 15 17 10" />
                                                        <line x1="12" y1="15" x2="12" y2="3" />
                                                    </svg>
                                                    Download
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {isSingleton && (headRow.superseded_versions || []).length > 0 && (
                                    <div className="mt-3"
                                         data-testid={`portal-executed-doc-${headRow.file_id}-prior-wrap`}>
                                        <button
                                            type="button"
                                            onClick={() => setExpandedPrior(p => ({...p, [headRow.file_id]: !p[headRow.file_id]}))}
                                            data-testid={`portal-executed-doc-${headRow.file_id}-prior-toggle`}
                                            className="text-[11.5px] text-[#A8543F] hover:text-[#8B4535] font-manrope font-semibold underline">
                                            {expandedPrior[headRow.file_id] ? "Hide" : "Show"} earlier versions
                                            {" "}({headRow.superseded_versions.length})
                                        </button>
                                        {expandedPrior[headRow.file_id] && (
                                            <ul className="mt-2 space-y-1 pl-3 border-l-2 border-[#EEEBE4]"
                                                data-testid={`portal-executed-doc-${headRow.file_id}-prior-list`}>
                                                {headRow.superseded_versions.map(sv => (
                                                    <li key={sv.version_id}
                                                        className="flex items-center gap-2 text-[11.5px] text-[#6B6B67]"
                                                        data-testid={`portal-executed-doc-${headRow.file_id}-prior-${sv.version_number}`}>
                                                        <span className="font-manrope font-semibold text-charcoal">
                                                            v{sv.version_number}
                                                        </span>
                                                        <span className="text-[#8A8A84]">·</span>
                                                        <span>{fmtSize(sv.size)}</span>
                                                        <span className="text-[#8A8A84]">·</span>
                                                        <span>{fmtDateShort(sv.uploaded_at)}</span>
                                                        <a
                                                            href={portalApi.executedDocumentDownloadUrl(headRow.file_id, sv.version_id)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            data-testid={`portal-executed-doc-${headRow.file_id}-prior-${sv.version_number}-download`}
                                                            className="ml-auto underline text-[#A8543F] hover:text-[#8B4535] font-manrope font-semibold">
                                                            Download
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>);
}
